/*
  File: src/components/layout/Navbar.jsx
  Purpose: Global navigation bar with responsive menus, cart preview, and profile actions.
  Responsibilities:
  - Desktop and mobile nav links; mobile slide-down menu and search.
  - Cart preview dropdown synced with `CartContext` and total display.
  - Auth-aware profile menu integrated with `AuthContext`.
  Accessibility:
  - Uses button semantics and aria-labels for interactive icons.
*/
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faMagnifyingGlass, faShoppingCart, faUser, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useCart } from '../../cart/CartContext';
import { useAuth } from '../../auth/AuthContext';
import FeatureUnavailableModal from '../modals/FeatureUnavailableModal';

function Navbar() {
    const { isAuthenticated, logout, user } = useAuth();
    // If context user missing avatar but localStorage has newer profile, use it as a fallback
    const localUser = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('auth:user') || 'null'); } catch { return null; }
    }, []);
    const effectiveUser = user || localUser;
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const cartRef = useRef(null);
    const navigate = useNavigate();
    const { items, total } = useCart();
    const [cartOpen, setCartOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const searchContainerRef = useRef(null);
    const listboxId = 'global-search-listbox';
    const [showAdminFeatureModal, setShowAdminFeatureModal] = useState(false);

    // Dev-only pages visibility flag: enable via REACT_APP_SHOW_DEV_PAGES=true
    // Defaults to true in non-production builds
    const SHOW_DEV_PAGES = useMemo(() => {
        const envVal = String(process.env.REACT_APP_SHOW_DEV_PAGES || '').trim().toLowerCase();
        if (envVal === 'true') return true;
        if (envVal === 'false') return false;
        return process.env.NODE_ENV !== 'production';
    }, []);

    // Administration enable/disable via REACT_APP_ENABLE_ADMINISTRATION=true|false
    // Defaults to false if not explicitly set
    const ENABLE_ADMIN = useMemo(() => {
        const envVal = String(process.env.REACT_APP_ENABLE_ADMINISTRATION || '').trim().toLowerCase();
        if (envVal === 'true') return true;
        if (envVal === 'false') return false;
        return false;
    }, []);

    // Check if management pages should be shown
    // Always show management pages (non-dev management tools are always available)
    const SHOW_MANAGEMENT = SHOW_DEV_PAGES;

    // Resolve API base similar to other pages
    const API_BASE = useMemo(() => {
        const raw = (process.env.REACT_APP_API_BASE || '').trim();
        const cleaned = raw.replace(/^@+/, '');
        return cleaned || '/dev';
    }, []);

    // Handle administration button click
    const handleAdministrationClick = (e) => {
        e.preventDefault();
        if (!ENABLE_ADMIN) {
            setShowAdminFeatureModal(true);
            return;
        }
        navigate('/dashboard');
    };

    useEffect(() => {
        const onDocClick = (e) => {
            // Only auto-close profile menu on desktop screens
            if (window.innerWidth < 768) return;
            if (!menuRef.current) return;
            if (!menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!cartRef.current) return;
            if (!cartRef.current.contains(e.target)) setCartOpen(false);
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    // Outside click for search dropdown
    useEffect(() => {
        const handler = (e) => {
            if (!searchContainerRef.current) return;
            if (!searchContainerRef.current.contains(e.target)) {
                setSearchOpen(false);
                setHighlightIndex(-1);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    // Debounced search
    useEffect(() => {
        let active = true;
        const q = searchQuery.trim();
        if (q.length < 2) {
            setSearchResults([]);
            setSearchLoading(false);
            return;
        }
        setSearchLoading(true);
        const t = setTimeout(async () => {
            try {
                const url = `${API_BASE}/search?q=${encodeURIComponent(q)}`;
                const res = await fetch(url, { headers: { accept: 'application/json' } });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                const results = Array.isArray(json?.results) ? json.results : [];
                if (active) {
                    setSearchResults(results);
                    setSearchLoading(false);
                    setSearchOpen(true);
                    setHighlightIndex(results.length > 0 ? 0 : -1);
                }
            } catch (_) {
                if (active) {
                    setSearchResults([]);
                    setSearchLoading(false);
                }
            }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [searchQuery, API_BASE]);

    const grouped = useMemo(() => {
        const products = [];
        const matches = [];
        const series = [];
        const players = [];
        for (const r of searchResults) {
            if (r?.type === 'product') products.push(r);
            else if (r?.type === 'match') matches.push(r);
            else if (r?.type === 'series') series.push(r);
            else if (r?.type === 'player') players.push(r);
        }
        return { products, players, matches, series };
    }, [searchResults]);

    const flatList = useMemo(() => {
        // Keep stable order: Products, Players, Matches, Series; include section headers as markers
        const out = [];
        if (grouped.products.length) out.push({ __header: 'Products' });
        out.push(...grouped.products);
        if (grouped.players.length) out.push({ __header: 'Players' });
        out.push(...grouped.players);
        if (grouped.matches.length) out.push({ __header: 'Matches' });
        out.push(...grouped.matches);
        if (grouped.series.length) out.push({ __header: 'Series' });
        out.push(...grouped.series);
        return out;
    }, [grouped]);

    const moveHighlight = (delta) => {
        if (!searchOpen || flatList.length === 0) return;
        let idx = highlightIndex;
        let attempts = 0;
        do {
            idx = (idx + delta + flatList.length) % flatList.length;
            attempts += 1;
        } while (flatList[idx]?.__header && attempts <= flatList.length + 2);
        setHighlightIndex(idx);
    };

    const labelFor = (item) => {
        if (!item) return '';
        if (item.type === 'product') return item.display_name || item.name || '';
        if (item.type === 'match' || item.type === 'series') return item.display_title || item.name || '';
        if (item.type === 'player') return item.display_name || (Array.isArray(item.names) ? item.names[0] : '') || '';
        return '';
    };

    const navigateFor = (item) => {
        if (!item) return;
        if (item.type === 'product') {
            navigate(`/store/${encodeURIComponent(item.id)}`);
        } else if (item.type === 'match') {
            navigate(`/football?scrollTo=${encodeURIComponent(item.id)}`);
        } else if (item.type === 'series') {
            navigate(`/tv?scrollTo=${encodeURIComponent(item.id)}`);
        } else if (item.type === 'player') {
            const pid = encodeURIComponent(item.id);
            try { localStorage.removeItem('storeFilters'); } catch { }
            // If already on /store, update the query params to trigger in-place filtering
            if (window.location.pathname.startsWith('/store')) {
                const sp = new URLSearchParams(window.location.search);
                sp.set('page', '1');
                sp.set('view', 'grid');
                sp.set('players', pid);
                navigate(`/store?${sp.toString()}`, { replace: false });
            } else {
                navigate(`/store?page=1&view=grid&players=${pid}`);
            }
        }
        setSearchOpen(false);
        setHighlightIndex(-1);
    };

    return (
        <header className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? 'backdrop-blur bg-backgroundDark/80 border-b border-white/10 shadow-[0_2px_20px_rgba(0,0,0,0.35)]' : 'bg-backgroundDark/60'}`}>
            <div className={`max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 ${scrolled ? 'h-12 sm:h-14' : 'h-14 sm:h-16'} flex items-center gap-2 sm:gap-4 transition-all`}>
                <div className="flex items-center gap-1 sm:gap-2 text-lg sm:text-xl font-extrabold tracking-wide">
                    <Link to="/" aria-label="Go to home">
                        <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="SceneShop" className="h-9 w-auto object-contain" />
                    </Link>
                </div>

                {/* Mobile actions */}
                <div className="md:hidden ml-auto flex items-center gap-2">
                    <button
                        aria-label="Open cart"
                        onClick={() => navigate('/cart')}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 relative"
                    >
                        <FontAwesomeIcon icon={faShoppingCart} className="text-sm" />
                        {items.length > 0 && (
                            <span className="absolute -top-1 -right-1 text-[10px] px-1 py-0.5 rounded-full bg-primary">{items.length}</span>
                        )}
                    </button>
                    <button
                        aria-label={mobileSearchOpen ? 'Close search' : 'Open search'}
                        onClick={() => setMobileSearchOpen(v => !v)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 border border-white/10"
                    >
                        <FontAwesomeIcon icon={mobileSearchOpen ? faXmark : faMagnifyingGlass} className="text-sm" />
                    </button>
                    <button
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        onClick={() => setMenuOpen(!menuOpen)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 border border-white/10"
                    >
                        <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} className="text-sm" />
                    </button>
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden md:flex items-center gap-4 lg:gap-6 text-sm font-semibold text-white/80">
                    <NavLink to="/" className={({ isActive }) => `hover:text-white relative ${isActive ? 'text-white' : ''}`}>
                        {({ isActive }) => (
                            <span className="inline-flex items-center">HOME{isActive && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-sky-400 align-middle" />}</span>
                        )}
                    </NavLink>
                    <NavLink to="/football" className={({ isActive }) => `hover:text-white relative ${isActive ? 'text-white' : ''}`}>
                        {({ isActive }) => (
                            <span className="inline-flex items-center">SPORTS{isActive && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-sky-400 align-middle" />}</span>
                        )}
                    </NavLink>
                    <NavLink to="/tv" className={({ isActive }) => `hover:text-white relative ${isActive ? 'text-white' : ''}`}>
                        {({ isActive }) => (
                            <span className="inline-flex items-center">TV SHOW{isActive && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-sky-400 align-middle" />}</span>
                        )}
                    </NavLink>
                    <NavLink to="/store" className={({ isActive }) => `hover:text-white relative ${isActive ? 'text-white' : ''}`}>
                        {({ isActive }) => (
                            <span className="inline-flex items-center">STORE{isActive && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-sky-400 align-middle" />}</span>
                        )}
                    </NavLink>
                    {/* Removed Rekognition Test from header */}
                </nav>

                <div className="ml-auto hidden md:flex items-center gap-2 sm:gap-3 w-full md:w-auto">
                    <div
                        className="relative flex items-center gap-1 sm:gap-2 bg-white/10 rounded-full px-2 sm:px-3 py-1.5 sm:py-1.5 w-full md:w-[240px] lg:w-[320px]"
                        ref={searchContainerRef}
                        role="combobox"
                        aria-haspopup="listbox"
                        aria-owns={listboxId}
                        aria-expanded={searchOpen}
                        aria-controls={listboxId}
                    >
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="text-white/80 text-xs sm:text-sm" />
                        <input
                            aria-label="Search"
                            aria-autocomplete="list"
                            aria-controls={listboxId}
                            className="bg-transparent outline-none text-white/90 placeholder-white/60 flex-1 hidden sm:block text-sm"
                            placeholder="Search products, matches, series"
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); }}
                            onFocus={() => { if ((searchResults?.length || 0) > 0) setSearchOpen(true); }}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowDown') { e.preventDefault(); moveHighlight(1); }
                                else if (e.key === 'ArrowUp') { e.preventDefault(); moveHighlight(-1); }
                                else if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const item = flatList[highlightIndex];
                                    if (item && !item.__header) navigateFor(item);
                                } else if (e.key === 'Escape') {
                                    setSearchOpen(false); setHighlightIndex(-1);
                                }
                            }}
                        />
                        {searchOpen && (
                            <div className="absolute left-0 right-0 top-full mt-1 rounded-xl border border-white/10 bg-backgroundDark shadow-2xl overflow-hidden z-50">
                                <div className="max-h-[60vh] overflow-auto" role="listbox" id={listboxId} aria-label="Search results">
                                    {searchLoading && (
                                        <div className="px-3 py-2 text-xs text-white/70">Loading...</div>
                                    )}
                                    {!searchLoading && flatList.length === 0 && searchQuery.trim().length >= 2 && (
                                        <div className="px-3 py-2 text-xs text-white/70">No results</div>
                                    )}
                                    {!searchLoading && flatList.length > 0 && (
                                        <div className="py-1">
                                            {flatList.map((item, idx) => {
                                                if (item.__header) {
                                                    return (
                                                        <div key={`h-${idx}`} className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-white/50">
                                                            {item.__header}
                                                        </div>
                                                    );
                                                }
                                                const isActive = idx === highlightIndex;
                                                const label = labelFor(item);
                                                const img = item.image_url;
                                                return (
                                                    <div
                                                        key={`${item.type}-${item.id}-${idx}`}
                                                        role="option"
                                                        aria-selected={isActive}
                                                        className={`px-3 py-2 text-xs sm:text-sm flex items-center gap-2 cursor-pointer ${isActive ? 'bg-white/10' : 'hover:bg-white/5'}`}
                                                        onMouseEnter={() => setHighlightIndex(idx)}
                                                        onMouseDown={(e) => { e.preventDefault(); }}
                                                        onClick={() => navigateFor(item)}
                                                    >
                                                        {img ? (
                                                            <img src={img} alt="" className="w-7 h-7 rounded object-cover bg-white/10" />
                                                        ) : (
                                                            <div className="w-7 h-7 rounded bg-white/10 flex items-center justify-center text-[10px] text-white/60 uppercase">
                                                                {item.type?.slice(0, 1)}
                                                            </div>
                                                        )}
                                                        <div className="min-w-0 flex-1">
                                                            <div className="truncate">{label}</div>
                                                            <div className="text-[10px] text-white/50 capitalize">{item.type}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="relative flex items-center gap-1 sm:gap-2" ref={cartRef}>
                        <button onClick={() => setCartOpen((v) => !v)} className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 relative transition-colors">
                            <FontAwesomeIcon icon={faShoppingCart} className="text-sm sm:text-base" />
                            {items.length > 0 && (
                                <span className="absolute -top-1 -right-1 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded-full bg-primary">{items.length}</span>
                            )}
                        </button>
                        {cartOpen && (
                            <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 rounded-xl border border-white/10 bg-backgroundDark shadow-2xl overflow-hidden z-50 origin-top-right transition transform scale-100 opacity-100">
                                <div className="max-h-[50vh] sm:max-h-[60vh] overflow-auto scrollbar-hide">
                                    {items.length === 0 && <div className="p-3 text-xs sm:text-sm text-white/70">Cart is empty</div>}
                                    {items.map(i => (
                                        <div key={`${i.id}-${i.size}-${i.color || 'nc'}-${i.variantId || 'nv'}`} className="flex items-center gap-3 p-3 border-b border-white/10 hover:bg-white/5 transition-colors">
                                            <div className="w-11 h-11 sm:w-12 sm:h-12 bg-white/10 rounded-lg overflow-hidden ring-1 ring-white/10">
                                                {i.imageUrl ? <img src={i.imageUrl} alt={i.name} className="w-full h-full object-cover" /> : null}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-semibold truncate">{i.name}</div>
                                                <div className="text-[10px] sm:text-[11px] text-white/70">{i.color ? `Color ${i.color} • ` : ''}Size {i.size} • Qty {i.qty}</div>
                                            </div>
                                            <div className="text-xs font-semibold whitespace-nowrap">{i.currency} {(Number(i.price || 0) * (i.qty || 1)).toFixed(2)}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="p-3 flex items-center justify-between text-xs sm:text-sm bg-white/5">
                                    <span>Total</span>
                                    <span>{items.length > 0 ? items[0].currency : '€'} {Number(total || 0).toFixed(2)}</span>
                                </div>
                                <div className="p-3 pt-2 flex gap-2">
                                    <button onClick={() => { setCartOpen(false); navigate('/cart'); }} className="btn-primary flex-1 rounded-lg py-2 text-xs sm:text-sm">View Cart</button>
                                    <button onClick={() => { setCartOpen(false); navigate('/cart'); }} className="btn-ghost flex-1 rounded-lg py-2 text-xs sm:text-sm">Checkout</button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="relative hidden md:block" ref={menuRef}>
                        <button onClick={() => setMenuOpen((v) => !v)} className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 overflow-hidden">
                            {isAuthenticated && effectiveUser?.avatar_url ? (
                                <img src={effectiveUser.avatar_url} alt="me" className="w-full h-full object-cover" />
                            ) : (
                                <FontAwesomeIcon icon={faUser} className="text-sm sm:text-base" />
                            )}
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 mt-2 w-40 sm:w-48 rounded-lg border border-white/10 bg-backgroundDark shadow-xl overflow-hidden z-50">
                                {!isAuthenticated && (
                                    <div className="py-1 text-xs sm:text-sm">
                                        <Link to="/login" className="block px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-white/10">Login</Link>
                                        <Link to="/signup" className="block px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-white/10">Sign up</Link>
                                    </div>
                                )}
                                {isAuthenticated && (
                                    <div className="py-1 text-xs sm:text-sm">
                                        <Link to="/profile" className="block px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-white/10">Profile</Link>
                                        <Link to="/orders" className="block px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-white/10">My Orders</Link>
                                        {SHOW_MANAGEMENT && (
                                            <Link to="/management" className="block px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-white/10">Management & Testing</Link>
                                        )}
                                        <button 
                                            onClick={handleAdministrationClick}
                                            className="w-full text-left block px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-white/10"
                                        >
                                            Administration
                                        </button>
                                        <button onClick={() => { logout(); navigate('/login'); }} className="w-full text-left px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-white/10">Logout</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {mobileSearchOpen && (
                    <div className="md:hidden absolute left-0 right-0 top-full border-t border-white/10 bg-backgroundDark">
                        <div className="px-3 py-3">
                            <div className="flex items-start gap-2">
                                <div className="relative flex-1" ref={searchContainerRef}>
                                    <div
                                        className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-2"
                                        role="combobox"
                                        aria-haspopup="listbox"
                                        aria-owns={`${listboxId}-m`}
                                        aria-expanded={searchOpen}
                                        aria-controls={`${listboxId}-m`}
                                    >
                                        <FontAwesomeIcon icon={faMagnifyingGlass} className="text-sm text-white/80" />
                                        <input
                                            autoFocus
                                            aria-label="Search"
                                            aria-autocomplete="list"
                                            aria-controls={`${listboxId}-m`}
                                            placeholder="Search products, matches, series"
                                            className="flex-1 bg-transparent outline-none text-white text-sm"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onFocus={() => { if ((searchResults?.length || 0) > 0) setSearchOpen(true); }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'ArrowDown') { e.preventDefault(); moveHighlight(1); }
                                                else if (e.key === 'ArrowUp') { e.preventDefault(); moveHighlight(-1); }
                                                else if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    const item = flatList[highlightIndex];
                                                    if (item && !item.__header) navigateFor(item);
                                                } else if (e.key === 'Escape') {
                                                    setSearchOpen(false); setHighlightIndex(-1);
                                                }
                                            }}
                                        />
                                    </div>
                                    {searchOpen && (
                                        <div className="absolute left-0 right-0 mt-2 rounded-xl border border-white/10 bg-backgroundDark shadow-2xl overflow-hidden z-50">
                                            <div className="max-h-[60vh] overflow-auto" role="listbox" id={`${listboxId}-m`} aria-label="Search results">
                                                {searchLoading && (
                                                    <div className="px-3 py-2 text-xs text-white/70">Loading...</div>
                                                )}
                                                {!searchLoading && flatList.length === 0 && searchQuery.trim().length >= 2 && (
                                                    <div className="px-3 py-2 text-xs text-white/70">No results</div>
                                                )}
                                                {!searchLoading && flatList.length > 0 && (
                                                    <div className="py-1">
                                                        {flatList.map((item, idx) => {
                                                            if (item.__header) {
                                                                return (
                                                                    <div key={`mh-${idx}`} className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-white/50">
                                                                        {item.__header}
                                                                    </div>
                                                                );
                                                            }
                                                            const isActive = idx === highlightIndex;
                                                            const label = labelFor(item);
                                                            const img = item.image_url;
                                                            return (
                                                                <div
                                                                    key={`m-${item.type}-${item.id}-${idx}`}
                                                                    role="option"
                                                                    aria-selected={isActive}
                                                                    className={`px-3 py-2 text-xs flex items-center gap-2 cursor-pointer ${isActive ? 'bg-white/10' : 'hover:bg-white/5'}`}
                                                                    onMouseEnter={() => setHighlightIndex(idx)}
                                                                    onMouseDown={(e) => { e.preventDefault(); }}
                                                                    onClick={() => { navigateFor(item); setMobileSearchOpen(false); }}
                                                                >
                                                                    {img ? (
                                                                        <img src={img} alt="" className="w-7 h-7 rounded object-cover bg-white/10" />
                                                                    ) : (
                                                                        <div className="w-7 h-7 rounded bg-white/10 flex items-center justify-center text-[10px] text-white/60 uppercase">
                                                                            {item.type?.slice(0, 1)}
                                                                        </div>
                                                                    )}
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="truncate">{label}</div>
                                                                        <div className="text-[10px] text-white/50 capitalize">{item.type}</div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <button
                                    aria-label={mobileSearchOpen ? 'Close search' : 'Open search'}
                                    onClick={() => { setMobileSearchOpen(false); setSearchOpen(false); }}
                                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/10"
                                >
                                    <FontAwesomeIcon icon={faXmark} className="text-sm" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {menuOpen && (
                    <div className="md:hidden fixed inset-0 z-40">
                        <div className="absolute inset-0 bg-black/70" onClick={() => setMenuOpen(false)} />
                        <div className="absolute inset-x-0 top-0 pt-16 pb-6 bg-backgroundDark border-b border-white/10">
                            <nav className="px-4 space-y-2">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="text-xs text-white/60">Menu</div>
                                    <button onClick={() => navigate('/cart')} className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 text-xs">
                                        <FontAwesomeIcon icon={faShoppingCart} />
                                        <span>Cart</span>
                                        {items.length > 0 && (
                                            <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-primary text-[10px]">{items.length}</span>
                                        )}
                                    </button>
                                </div>
                                <NavLink
                                    to="/"
                                    onClick={() => { setMenuOpen(false); setMobileSearchOpen(false); }}
                                    className={({ isActive }) => `block px-4 py-3 rounded-lg text-base font-semibold ${isActive ? 'text-white bg-white/10' : 'text-white/80 hover:text-white hover:bg-white/5'}`}
                                >
                                    HOME
                                </NavLink>
                                <NavLink
                                    to="/football"
                                    onClick={() => { setMenuOpen(false); setMobileSearchOpen(false); }}
                                    className={({ isActive }) => `block px-4 py-3 rounded-lg text-base font-semibold ${isActive ? 'text-white bg-white/10' : 'text-white/80 hover:text-white hover:bg-white/5'}`}
                                >
                                    FOOTBALL
                                </NavLink>
                                <NavLink
                                    to="/tv"
                                    onClick={() => { setMenuOpen(false); setMobileSearchOpen(false); }}
                                    className={({ isActive }) => `block px-4 py-3 rounded-lg text-base font-semibold ${isActive ? 'text-white bg-white/10' : 'text-white/80 hover:text-white hover:bg-white/5'}`}
                                >
                                    TV SHOW
                                </NavLink>
                                <NavLink
                                    to="/store"
                                    onClick={() => { setMenuOpen(false); setMobileSearchOpen(false); }}
                                    className={({ isActive }) => `block px-4 py-3 rounded-lg text-base font-semibold ${isActive ? 'text-white bg-white/10' : 'text-white/80 hover:text-white hover:bg-white/5'}`}
                                >
                                    STORE
                                </NavLink>
                                <div className="pt-2 border-t border-white/10 mt-2">
                                    {!isAuthenticated ? (
                                        <div className="grid grid-cols-2 gap-2">
                                            <Link to="/login" onClick={() => { setMenuOpen(false); setMobileSearchOpen(false); }} className="btn-ghost text-center text-sm font-semibold rounded-lg py-3">Login</Link>
                                            <Link to="/signup" onClick={() => { setMenuOpen(false); setMobileSearchOpen(false); }} className="btn-primary text-center text-sm font-semibold rounded-lg py-3">Sign up</Link>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-2">
                                            <button 
                                                onClick={(e) => {
                                                    setMenuOpen(false); 
                                                    setMobileSearchOpen(false);
                                                    handleAdministrationClick(e);
                                                }} 
                                                className="btn-ghost text-center text-sm font-semibold rounded-lg py-3"
                                            >
                                                Administration
                                            </button>
                                            <Link to="/profile" onClick={() => { setMenuOpen(false); setMobileSearchOpen(false); }} className="btn-ghost text-center text-sm font-semibold rounded-lg py-3">Profile</Link>
                                            <Link to="/orders" onClick={() => { setMenuOpen(false); setMobileSearchOpen(false); }} className="btn-ghost text-center text-sm font-semibold rounded-lg py-3">My Orders</Link>
                                            {SHOW_MANAGEMENT && (
                                                <Link to="/management" onClick={() => { setMenuOpen(false); setMobileSearchOpen(false); }} className="btn-ghost text-center text-sm font-semibold rounded-lg py-3">Management & Testing</Link>
                                            )}
                                            <button onClick={() => { logout(); setMenuOpen(false); setMobileSearchOpen(false); navigate('/login'); }} className="col-span-2 btn-ghost text-center text-sm font-semibold rounded-lg py-3">Logout</button>
                                        </div>
                                    )}
                                </div>
                            </nav>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Feature Unavailable Modal for Administration */}
            {showAdminFeatureModal && (
                <FeatureUnavailableModal 
                    isOpen={showAdminFeatureModal} 
                    onClose={() => setShowAdminFeatureModal(false)} 
                />
            )}
        </header>
    );
}

export default Navbar;
